import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Switch, useToast } from '@artosera/ui';
import { usePeut } from '../auth/usePeut.js';
import { marquerEspaceTest } from '../lib/api.js';
import formStyles from '../styles/form.module.css';

/**
 * Espace de test (lot B) : une galerie hors de tout chiffre de pilotage et de
 * facturation. Le badge se montre à tous ; l'interrupteur à la direction de
 * Venio seule (`console:mark-test-gallery`), que l'API exige de son côté.
 */
export function BadgeTest(): JSX.Element {
  const { t } = useTranslation('console');
  return <Badge tone="accent">{t('espaceTest.badge')}</Badge>;
}

export function InterrupteurEspaceTest({
  idGalerie,
  estTest,
}: {
  idGalerie: string;
  estTest: boolean;
}): JSX.Element | null {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: (valeur: boolean) => marquerEspaceTest(idGalerie, valeur),
    onSuccess: async (reponse) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['console', 'galerie', idGalerie] }),
        queryClient.invalidateQueries({ queryKey: ['console', 'galeries'] }),
      ]);
      toast({
        title: reponse.estTest ? t('espaceTest.marque') : t('espaceTest.demarque'),
        closeLabel: t('saisies.suppression.fermer'),
      });
    },
    onError: () => {
      toast({
        title: t('espaceTest.erreur'),
        tone: 'danger',
        closeLabel: t('saisies.suppression.fermer'),
      });
    },
  });

  if (!peut('console:mark-test-gallery')) return null;

  return (
    <div>
      <Switch
        id="fiche-espace-test"
        label={t('espaceTest.interrupteur')}
        checked={estTest}
        disabled={mutation.isPending}
        onCheckedChange={(valeur) => mutation.mutate(valeur)}
        aria-describedby="fiche-espace-test-aide"
      />
      <p id="fiche-espace-test-aide" className={formStyles.aide}>
        {t('espaceTest.aide')}
      </p>
    </div>
  );
}
